import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { eventsApi } from '@/services/events.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Creates an event.
 *
 * Starts as a draft: only name, start and end are required. Registration
 * opens only once the organizer publishes it — see the detail page — so a
 * half-planned event never accidentally accepts sign-ups.
 */
export function EventForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    location: '',
    startAt: '',
    endAt: '',
    capacity: '',
    registrationRequired: true,
  });

  useEffect(() => {
    // `showModal` rather than `show`: it traps focus, renders the backdrop and
    // makes Escape close the dialog, all without a focus-trap library.
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      eventsApi.create({
        name: form.name.trim(),
        description: form.description.trim() || null,
        category: form.category.trim() || null,
        location: form.location.trim() || null,
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
        // An empty input is "no limit", not zero. The API rejects zero.
        capacity: form.capacity ? Number(form.capacity) : null,
        registrationRequired: form.registrationRequired,
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors;
        setFieldErrors(fields);
        if (Object.keys(fields).length === 0) {
          setFormError(t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') }));
        }
        return;
      }
      setFormError(t('errors.INTERNAL_ERROR'));
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);
    mutation.mutate();
  }

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="event-form-title">
      <form onSubmit={handleSubmit} noValidate className="dialog-body">
        <h2 id="event-form-title">{t('events.event.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('events.event.name')}
          name="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={fieldErrors.name}
          required
          autoFocus
        />

        <Field
          label={t('events.event.description')}
          name="description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          error={fieldErrors.description}
        />

        <div className="form-grid">
          <Field
            label={t('events.event.startAt')}
            name="startAt"
            type="datetime-local"
            value={form.startAt}
            onChange={(e) => setForm({ ...form, startAt: e.target.value })}
            error={fieldErrors.startAt}
            required
            dir="ltr"
          />

          <Field
            label={t('events.event.endAt')}
            name="endAt"
            type="datetime-local"
            value={form.endAt}
            onChange={(e) => setForm({ ...form, endAt: e.target.value })}
            error={fieldErrors.endAt}
            required
            dir="ltr"
          />
        </div>

        <div className="form-grid">
          <Field
            label={t('events.event.category')}
            name="category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            error={fieldErrors.category}
          />

          <Field
            label={t('events.event.location')}
            name="location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            error={fieldErrors.location}
          />
        </div>

        <Field
          label={t('events.event.capacity')}
          hint={t('events.event.capacityHint')}
          name="capacity"
          type="number"
          min={1}
          value={form.capacity}
          onChange={(e) => setForm({ ...form, capacity: e.target.value })}
          error={fieldErrors.capacity}
          dir="ltr"
        />

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.registrationRequired}
            onChange={(e) => setForm({ ...form, registrationRequired: e.target.checked })}
          />
          <span>{t('events.event.registrationRequired')}</span>
        </label>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
